// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// useRecorder — MediaRecorder lifecycle hook.
//
// Encapsulates microphone permission handling, MediaRecorder start/stop,
// chunk collection, and elapsed-time tracking.
//
// The hook is intentionally narrow in scope — it only deals with capturing
// audio. Upload and transcription are handled separately by transcribe.ts
// and the Recorder component.
//
// State machine:
//   idle → [requestPermission] → requesting
//   requesting → [granted]  → ready
//   requesting → [denied]   → denied
//   ready → [start]         → recording
//   recording → [stop]      → stopped (audioBlob available)
//   stopped → [reset]       → ready
//   * → [reset]             → ready (clears blob, resets timer)

import { useCallback, useEffect, useRef, useState } from "react";
import { getBestAudioMimeType } from "./transcribe";

// ── Types ─────────────────────────────────────────────────────────────────────

export type MicPermission =
  | "idle"         // not yet requested
  | "requesting"   // awaiting getUserMedia
  | "granted"      // permission granted, stream active
  | "denied"       // user denied or browser blocked
  | "unavailable"; // MediaRecorder or getUserMedia not supported

export type RecordingState =
  | "idle"       // ready but not recording
  | "recording"  // actively recording
  | "stopped";   // recording finished, blob available

export interface UseRecorderResult {
  /** Microphone permission / availability status. */
  micPermission: MicPermission;
  /** Current recording state. */
  recordingState: RecordingState;
  /** Elapsed seconds since recording started (updates every second). */
  elapsedSeconds: number;
  /** The captured audio Blob. Only set when recordingState === 'stopped'. */
  audioBlob: Blob | null;
  /** The MIME type actually used (e.g. 'audio/webm;codecs=opus'). */
  mimeType: string | null;
  /** The file extension for the storage path (e.g. 'webm', 'm4a'). */
  extension: string | null;
  /** Request microphone access. Call once on user interaction. */
  requestPermission: () => Promise<void>;
  /** Start recording. Requires permission === 'granted'. */
  start: () => void;
  /** Stop recording and collect the audio blob. */
  stop: () => void;
  /** Reset back to idle/ready state, clearing any blob and timer. */
  reset: () => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useRecorder(): UseRecorderResult {
  const [micPermission, setMicPermission] = useState<MicPermission>(() => {
    // Check static support at hook init — avoids requesting before needed
    if (
      typeof MediaRecorder === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      return "unavailable";
    }
    return "idle";
  });

  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [mimeType, setMimeType] = useState<string | null>(null);
  const [extension, setExtension] = useState<string | null>(null);

  // Refs: stable across renders, no re-render on assignment
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup media stream and timer on unmount
  useEffect(() => {
    return () => {
      stopTimer();
      releaseStream();
    };
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  function stopTimer() {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function releaseStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  // ── requestPermission ────────────────────────────────────────────────────────

  const requestPermission = useCallback(async () => {
    if (micPermission === "unavailable") return;
    setMicPermission("requesting");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setMicPermission("granted");
    } catch (err) {
      console.warn("[useRecorder] getUserMedia error:", err);
      // NotAllowedError / PermissionDeniedError → user denied
      // Other errors (NotFoundError, etc.) → treat as unavailable
      const name = err instanceof Error ? err.name : "";
      setMicPermission(
        name === "NotAllowedError" || name === "PermissionDeniedError"
          ? "denied"
          : "unavailable",
      );
    }
  }, [micPermission]);

  // ── start ────────────────────────────────────────────────────────────────────

  const start = useCallback(() => {
    if (micPermission !== "granted" || recordingState === "recording") return;

    const mimeInfo = getBestAudioMimeType();
    if (!mimeInfo) {
      setMicPermission("unavailable");
      return;
    }

    // Re-acquire stream if it was released by a prior reset
    const getStream = (): Promise<MediaStream> => {
      if (streamRef.current) return Promise.resolve(streamRef.current);
      return navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
        streamRef.current = s;
        return s;
      });
    };

    getStream()
      .then((stream) => {
        chunksRef.current = [];

        const recorder = new MediaRecorder(stream, {
          mimeType: mimeInfo.mimeType,
        });

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data);
          }
        };

        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, {
            type: mimeInfo.mimeType,
          });
          setAudioBlob(blob);
          setRecordingState("stopped");
          stopTimer();
          releaseStream();
        };

        recorder.onerror = (e) => {
          console.error("[useRecorder] MediaRecorder error:", e);
          setRecordingState("idle");
          stopTimer();
        };

        mediaRecorderRef.current = recorder;
        setMimeType(mimeInfo.mimeType);
        setExtension(mimeInfo.extension);
        setElapsedSeconds(0);
        setRecordingState("recording");

        recorder.start(250); // collect chunks every 250 ms

        // Elapsed timer
        const startTime = Date.now();
        timerRef.current = setInterval(() => {
          setElapsedSeconds(Math.floor((Date.now() - startTime) / 1000));
        }, 1000);
      })
      .catch((err) => {
        console.error("[useRecorder] Failed to acquire stream for recording:", err);
        setMicPermission("denied");
      });
  }, [micPermission, recordingState]);

  // ── stop ────────────────────────────────────────────────────────────────────

  const stop = useCallback(() => {
    if (recordingState !== "recording") return;
    mediaRecorderRef.current?.stop();
    // onstop handler on the recorder sets state → 'stopped'
  }, [recordingState]);

  // ── reset ───────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    // Stop any active recording cleanly
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    stopTimer();
    releaseStream();
    chunksRef.current = [];
    mediaRecorderRef.current = null;
    setAudioBlob(null);
    setElapsedSeconds(0);
    setRecordingState("idle");
    // Keep micPermission as-is (don't re-request if already granted/denied)
  }, []);

  return {
    micPermission,
    recordingState,
    elapsedSeconds,
    audioBlob,
    mimeType,
    extension,
    requestPermission,
    start,
    stop,
    reset,
  };
}
